import { type ReactNode } from 'react';
import { motion } from 'framer-motion';
import { cn } from '@/lib/utils';
import type { ViewMode } from '@/types/roadmap.types';

const SLIDE_EASE = [0.16, 1, 0.3, 1] as const;

interface SlideProps {
  children: ReactNode;
  className?: string;
  mode: ViewMode;
  direction?: 1 | -1;
  wide?: boolean;
}

export default function Slide({
  children,
  className,
  mode,
  direction = 1,
  wide = false,
}: SlideProps) {
  const isPresentation = mode === 'presentation';

  const maxW = wide ? 'max-w-[1536px]' : 'max-w-7xl';

  const content = (
    <div
      className={cn(
        'mx-auto w-full',
        maxW,
        isPresentation
          ? 'min-h-full flex items-start justify-center p-6 sm:p-8 lg:p-12 print:min-h-0 print:p-0 print:block'
          : 'py-12 sm:py-16 px-6 sm:px-8 print:py-0 print:px-0',
        className,
      )}
    >
      {isPresentation ? (
        <div className={cn('mx-auto w-full my-auto print:my-0', maxW)}>
          {children}
        </div>
      ) : (
        children
      )}
    </div>
  );

  if (!isPresentation) {
    return <section>{content}</section>;
  }

  return (
    <motion.section
      className="w-full"
      initial={{ opacity: 0, x: 80 * direction }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -80 * direction }}
      transition={{ duration: 0.5, ease: SLIDE_EASE as unknown as number[] }}
    >
      {content}
    </motion.section>
  );
}
